import { getStudents } from '@/app/services/reports/reports';
import { getAllRooms } from '@/app/services/rooms/rooms';
import { getAlbums } from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import FilterSolidIcon from '@shared/icons/FilterSolidIcon';
import { Divider } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PopoverCombobox from '@shared/Reports/PopoverCombobox';
import PopoverDateRange from '@shared/Reports/PopoverDateRange';
import PopoverSelect from '@shared/Reports/PopoverSelect';

const GalleryFiltersBar = ({ filters, setFilters, tab = 'photos' }) => {
	const [rooms, setRooms] = useState([]);
	const [roomsLoading, setRoomsLoading] = useState(true);
	const [students, setStudents] = useState([]);
	const [studentsLoading, setStudentsLoading] = useState(true);
	const [allStdPage, setAllStdPage] = useState(1);
	const [albums, setAlbums] = useState([]);
	const [albumsLoading, setAlbumsLoading] = useState(true);
	const dispatch = useDispatch();

	useEffect(() => {
		const fetchRooms = async () => {
			try {
				setRoomsLoading(true);
				const res = await getAllRooms();
				setRooms(res.data);
				setRoomsLoading(false);
			} catch (error) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch rooms',
						variant: 'error',
					})
				);
				setRoomsLoading(false);
			}
		};
		fetchRooms();
	}, []);

	useEffect(() => {
		if (tab !== 'photos') return;
		let isMounted = true;
		const fetchStudents = async () => {
			try {
				const res = await getStudents('', allStdPage);
				setStudentsLoading(false);
				if (!isMounted) return;
				setStudents((prev) => [...prev, ...res.data.data]);
				if (res.data.current_page < res.data.last_page) {
					setAllStdPage((p) => p + 1);
				}
			} catch (error) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch students',
						variant: 'error',
					})
				);
				setStudentsLoading(false);
			}
		};
		fetchStudents();
		return () => {
			isMounted = false;
		};
	}, [allStdPage, tab]);

	useEffect(() => {
		if (tab !== 'photos') return;
		const fetchAlbums = async () => {
			try {
				setAlbumsLoading(true);
				const res = await getAlbums({ per_page: 100 });
				const data = res.data.data || res.data || [];
				setAlbums(data);
				setAlbumsLoading(false);
			} catch (error) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch albums',
						variant: 'error',
					})
				);
				setAlbumsLoading(false);
			}
		};
		fetchAlbums();
	}, [tab]);

	return (
		<div className="py-6 md:py-10 px-24 md:px-32 justify-between flex flex-wrap bg-[#002E6C] text-white border-b border-b-white">
			<span className="flex items-center gap-8 text-14 text-white">
				<FilterSolidIcon className="size-16" /> Filters:
			</span>
			<div className="flex gap-8 md:gap-16 flex-wrap justify-end">
				{tab === 'photos' && (
					<>
						<PopoverDateRange compact filters={filters} setFilters={setFilters} />
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
					</>
				)}
				<PopoverCombobox
					compact
					currentFilter="room_id"
					filters={filters}
					options={
						roomsLoading
							? []
							: rooms?.map((room) => ({
									label: room?.name,
									value: room?.id,
								}))
					}
					setFilter={setFilters}
					showAllOnOpen
					title="Room"
				/>
				{tab === 'photos' && (
					<>
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
						<PopoverCombobox
							compact
							currentFilter="student_id"
							filters={filters}
							options={
								studentsLoading
									? []
									: students?.map((student) => ({
											label: `${student?.first_name} ${student?.last_name}`,
											value: student?.id,
										}))
							}
							setFilter={setFilters}
							title="Student"
						/>
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
						<PopoverCombobox
							compact
							currentFilter="activity_id"
							filters={filters}
							options={[
								{ label: 'Snap a Pic', value: 13 },
								{ label: 'Share a Clip', value: 14 },
								{ label: 'Diaper', value: 1 },
								{ label: 'Meals', value: 2 },
								{ label: 'Nap', value: 3 },
								{ label: 'Potty', value: 4 },
								{ label: 'Bottle', value: 5 },
								{ label: 'Lessons', value: 6 },
								{ label: 'Centers', value: 7 },
								{ label: 'Outdoor Learning', value: 8 },
								{ label: 'Kudos', value: 12 },
								{ label: 'Supplies', value: 10 },
								{ label: 'Facelink\u2122', value: 9 },
								{ label: 'Post', value: 11 },
							]}
							setFilter={setFilters}
							showAllOnOpen
							title="Activity"
						/>
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
						<PopoverCombobox
							compact
							currentFilter="album_id"
							filters={filters}
							options={
								albumsLoading
									? []
									: albums?.map((album) => ({
											label: album?.name,
											value: album?.id,
										}))
							}
							setFilter={setFilters}
							showAllOnOpen
							title="Album"
						/>
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
						<PopoverSelect
							compact
							currentFilter="media_type"
							filters={filters}
							options={[
								{ label: 'All', value: 'all' },
								{ label: 'Photos', value: 'image' },
								{ label: 'Videos', value: 'video' },
							]}
							setFilter={setFilters}
							title="Type"
						/>
					</>
				)}
				{tab === 'albums' && (
					<>
						<Divider className="bg-gray-200" flexItem orientation="vertical" />
						<PopoverSelect
							compact
							currentFilter="type"
							filters={filters}
							options={[
								{ label: 'All Albums', value: 'all' },
								{ label: 'Manual Albums', value: 'manual' },
								{ label: 'Smart Albums', value: 'smart' },
							]}
							setFilter={setFilters}
							title="Album Type"
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default GalleryFiltersBar;
